
import { Component, Vue } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import Sessions from "@/components/Sessions.vue";
import EventSearch from "@/components/EventSearch.vue";
import EventReview from "@/components/EventReview.vue";
import { EventSearchResult, EventSearchState } from "@/store/event-search/types";

import NavHeader from "@/components/NavHeader.vue";
import api from "@/services/api.service";

import { FeaturesList } from "@/store/types";
import { get } from "lodash";
import { formatDateMixin } from "@/mixins.ts";
import moment from "moment";

const EventSearchStore = namespace("eventSearch");
const SiteMonitorStore = namespace("siteMonitor");

Vue.mixin(formatDateMixin);
const { displayTimeFromISOString, convertUtcDateTimeToLocalTimeString, displayDateFromISOString } = formatDateMixin.methods;

@Component({
	components: {
		"event-search": EventSearch,
		"event-review": EventReview,
		session: Sessions,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"nav-header": NavHeader
	}
})
export default class EventSearchPage extends Vue {

	@Getter("getFeaturesList") featuresList: FeaturesList;

	@EventSearchStore.Getter events: EventSearchResult[];
	@EventSearchStore.Action loadEvents: any;
	@EventSearchStore.Getter searching: boolean;
	@EventSearchStore.Mutation setEvents: any;
	@EventSearchStore.Action loadDateTimePrefs: () => Promise<void>;
	@EventSearchStore.State((state: EventSearchState) => state.prefs.localEventTimePref) localTimePref: boolean;
	@EventSearchStore.State((state: EventSearchState) => state.prefs.eventDatePref) eventDatePref: boolean;

	@SiteMonitorStore.Mutation resetEventRecords: any;

	public selectedEvent = null;
	public makeSearchRequest = false;
	public eventGoto = "";

	private get isEventSearched() {
		return !!this.$route.params.eventId ?? false;
	}

	public get selectedEventId() {
		return this.selectedEvent && this.selectedEvent.eventID ? this.selectedEvent.eventID : 0;
	}

	public get isEmptySearchResults() {
		return this.makeSearchRequest && this.events.length === 0;
	}

	public get isSearchCollapsed() {
		return this.makeSearchRequest && this.events.length > 0;
	}

	public get isEventFlaggingEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "EventFlagging"], false);
	}

	public async mounted() {
		await this.loadDateTimePrefs();
		if (this.$route.params.eventId) {
			var eventId = parseInt(this.$route.params.eventId);
			if (!isNaN(eventId)) this.selectedEvent = (await api.loadEventDetails(eventId)).data;
		}
	}

	public toSearch() {
		this.$router.push({ path: `/eventsearch` });
	}

	public async gotoEvent() {
		if (this.eventGoto) {
			var eventId = parseInt(this.eventGoto);
			if (!isNaN(eventId) && eventId < Number.MAX_SAFE_INTEGER) {
				var eventDetails = (await api.loadEventDetails(eventId)).data;
				if (eventDetails && eventDetails.closed) {
					this.makeSearchRequest = false;
					this.resetEventRecords();
					this.setEvents([]);

					this.selectedEvent = eventDetails;
					this.$router.push({
						path: `/eventsearch/${this.eventGoto}`
					});
					return;
				}
			}

			if (!eventDetails.closed) {
				this.$notify({
					type: "error",
					title: "Error",
					text: "This event is still being processed"
				});
			} else {
				this.$notify({
					type: "error",
					title: "Error",
					text: "This is not a valid ID"
				});
			}
		}
	}

	private resetSearch() {
		this.setEvents([]);
		this.selectedEvent = null;
		this.makeSearchRequest = false;
	}

	public beforeDestroy() {
		this.resetEventRecords();
		this.setEvents([]);
	}

	public eventSelect(event: any) {
		this.resetEventRecords();

		this.$set(this, "selectedEvent", event);
	}

	public onSearch(searchFilter: any) {
		this.loadEvents(searchFilter);
		this.makeSearchRequest = true;
		this.selectedEvent = null;
	}

	/**
	 * Formats the created date of the event depending on the preferences that are set
	 * If localTimePref is true we will display in the browsers timezone, false we display in the timezone of the event area
	 * If eventDatePref is true we will display the date and time, false we display just the time
	 * @param event The Event details to format the date for
	 */
	public formatDate(event: EventSearchResult): string {
		if (!event.created) {
			return "";
		}

		let timeDate: string;

		if (this.localTimePref) {
			timeDate =`${this.eventDatePref ? moment(event.created + 'z').format("Do MMM YYYY, LTS") : convertUtcDateTimeToLocalTimeString(event.created)}`
		} else {
			timeDate = `${this.eventDatePref ? moment(event.groupCreated).format("Do MMM YYYY, LTS") : displayTimeFromISOString(event.groupCreated)}`
		}

		let timeZone = "";
		if (event.timeZoneTitle || this.localTimePref) {
			timeZone = ` - ${event.timeZoneTitle}`
		}

		return timeDate + timeZone;
	}
}
